import { useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/use-current-user';
import { apiClient } from '@/utils/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'prosci-ui';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProximaDailyUsage {
  dates: string[];
  total: number[];
}

interface ProximaUsageResponse {
  data: {
    type: 'proxima_usage_stats';
    attributes: {
      daily_usage: ProximaDailyUsage;
    };
  };
}

interface TooltipState {
  index: number;
  xPx: number;
  yPx: number;
}

// ─── Layout constants ─────────────────────────────────────────────────────────

const CHART_HEIGHT = 200;
const PAD = { top: 12, right: 16, bottom: 36, left: 48 } as const;
const BRAND_COLOR = '#0E665E';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAxisDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Catmull-Rom → cubic Bézier smooth path through all points.
 * Points are in SVG viewBox space (0–100 on each axis).
 */
function smoothLinePath(points: Array<[number, number]>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0][0]},${points[0][1]}`;
  if (points.length === 2) {
    return `M ${points[0][0]},${points[0][1]} L ${points[1][0]},${points[1][1]}`;
  }

  let d = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const cp1x = (p1[0] + (p2[0] - p0[0]) / 6).toFixed(3);
    const cp1y = (p1[1] + (p2[1] - p0[1]) / 6).toFixed(3);
    const cp2x = (p2[0] - (p3[0] - p1[0]) / 6).toFixed(3);
    const cp2y = (p2[1] - (p3[1] - p1[1]) / 6).toFixed(3);

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0].toFixed(3)},${p2[1].toFixed(3)}`;
  }
  return d;
}

/** Evenly-spaced index positions to use as X-axis tick labels (~5 labels). */
function pickLabelIndices(n: number): number[] {
  if (n === 0) return [];
  if (n === 1) return [0];
  const step = Math.max(1, Math.round((n - 1) / 4));
  const indices = new Set<number>([0, n - 1]);
  for (let i = step; i < n - 1; i += step) indices.add(i);
  return Array.from(indices).sort((a, b) => a - b);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>API Usage</CardTitle>
        <CardDescription>Last 30 days</CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="animate-pulse rounded-md bg-muted"
          style={{ height: CHART_HEIGHT + PAD.top + PAD.bottom }}
        />
      </CardContent>
    </Card>
  );
}

function EmptyOrError({ message }: { message: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>API Usage</CardTitle>
        <CardDescription>Last 30 days</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="py-8 text-center text-sm text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}

// ─── Chart ────────────────────────────────────────────────────────────────────

interface ChartProps {
  dates: string[];
  totals: number[];
}

function UsageChart({ dates, totals }: ChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const n = dates.length;
  const maxVal = Math.max(...totals, 1);
  const yTicks = [0, Math.round(maxVal / 2), maxVal];
  const labelIndices = pickLabelIndices(n);

  // SVG viewBox points — x and y both 0–100
  const svgPoints: Array<[number, number]> = totals.map((val, i) => [
    n > 1 ? (i / (n - 1)) * 100 : 50,
    (1 - val / maxVal) * 100,
  ]);

  const linePath = smoothLinePath(svgPoints);
  const firstPt = svgPoints[0];
  const lastPt = svgPoints[n - 1];
  const areaPath = `${linePath} L ${lastPt[0].toFixed(3)},100 L ${firstPt[0].toFixed(3)},100 Z`;

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!chartRef.current) return;
      const rect = chartRef.current.getBoundingClientRect();
      const chartAreaWidth = rect.width - PAD.left - PAD.right;
      if (chartAreaWidth <= 0) return;

      const relX = e.clientX - rect.left - PAD.left;
      const xFraction = Math.max(0, Math.min(1, relX / chartAreaWidth));
      const idx = Math.max(0, Math.min(n - 1, Math.round(xFraction * (n - 1))));
      const idxXFraction = n > 1 ? idx / (n - 1) : 0.5;

      setTooltip({
        index: idx,
        xPx: PAD.left + idxXFraction * chartAreaWidth,
        yPx: PAD.top + (1 - totals[idx] / maxVal) * CHART_HEIGHT,
      });
    },
    [n, totals, maxVal]
  );

  const containerHeight = CHART_HEIGHT + PAD.top + PAD.bottom;

  return (
    <div
      ref={chartRef}
      className="relative select-none"
      style={{ height: containerHeight }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}
    >
      {/* Y-axis labels */}
      <div
        className="absolute left-0 top-0 flex flex-col justify-between text-xs text-muted-foreground"
        style={{ width: PAD.left, height: CHART_HEIGHT, paddingTop: PAD.top }}
      >
        {[...yTicks].reverse().map((tick) => (
          <span key={tick} className="block pr-2 text-right leading-none">
            {tick}
          </span>
        ))}
      </div>

      {/* SVG chart area */}
      <div
        className="absolute border-b border-l border-gray-200"
        style={{
          left: PAD.left,
          right: PAD.right,
          top: PAD.top,
          height: CHART_HEIGHT,
          overflow: 'visible',
        }}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="h-full w-full overflow-visible"
        >
          <defs>
            <linearGradient id="proxima-usage-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BRAND_COLOR} stopOpacity="0.2" />
              <stop offset="100%" stopColor={BRAND_COLOR} stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Mid grid line */}
          <line
            x1="0"
            y1="50"
            x2="100"
            y2="50"
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />

          {/* Filled area */}
          <path d={areaPath} fill="url(#proxima-usage-fill)" />

          {/* Smooth line */}
          <path
            d={linePath}
            fill="none"
            stroke={BRAND_COLOR}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* Hover vertical line (in SVG x-space so it stays aligned) */}
          {tooltip && (
            <line
              x1={svgPoints[tooltip.index][0]}
              y1="0"
              x2={svgPoints[tooltip.index][0]}
              y2="100"
              stroke={BRAND_COLOR}
              strokeWidth="1"
              strokeDasharray="3 3"
              strokeOpacity="0.5"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      </div>

      {/* Hover dot (DOM element avoids SVG non-scaling-radius issue) */}
      {tooltip && (
        <div
          className="pointer-events-none absolute rounded-full border-2 border-white shadow-sm"
          style={{
            width: 12,
            height: 12,
            background: BRAND_COLOR,
            left: tooltip.xPx - 6,
            top: tooltip.yPx - 6,
          }}
        />
      )}

      {/* Tooltip */}
      {tooltip && (() => {
        const chartAreaWidth =
          (chartRef.current?.getBoundingClientRect().width ?? 0) - PAD.left - PAD.right;
        // Keep tooltip from overflowing left/right edges of the container
        const clampedX = Math.max(
          PAD.left + 60,
          Math.min(tooltip.xPx, PAD.left + chartAreaWidth - 60)
        );
        const tipTop = Math.max(4, tooltip.yPx - 68);

        return (
          <div
            className="pointer-events-none absolute z-10 rounded-md border border-border bg-white px-3 py-2 shadow-md"
            style={{ left: clampedX, top: tipTop, transform: 'translateX(-50%)' }}
          >
            <p className="text-sm font-semibold leading-tight">
              {formatAxisDate(dates[tooltip.index])}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {totals[tooltip.index].toLocaleString()} requests
            </p>
          </div>
        );
      })()}

      {/* X-axis labels */}
      <div
        className="absolute text-xs text-muted-foreground"
        style={{
          left: PAD.left,
          right: PAD.right,
          top: PAD.top + CHART_HEIGHT + 8,
          height: PAD.bottom - 8,
        }}
      >
        {labelIndices.map((i) => (
          <span
            key={dates[i]}
            className="absolute whitespace-nowrap"
            style={{
              left: `${n > 1 ? (i / (n - 1)) * 100 : 50}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {formatAxisDate(dates[i])}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export function ProximaUsageHistory() {
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const organizationId = currentUser.organization_id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['proxima-usage', organizationId],
    queryFn: async () => {
      const response = await apiClient.get<ProximaUsageResponse>(
        '/api/external_api/proxima_usage',
        { organization_id: organizationId }
      );
      return response.data.attributes.daily_usage;
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  if (userLoading || isLoading) return <Skeleton />;
  if (isError) return <EmptyOrError message="Usage data unavailable." />;

  const dates = data?.dates ?? [];
  const totals = data?.total ?? [];
  const hasUsage = totals.some((v) => v > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Usage</CardTitle>
        <CardDescription>Last 30 days</CardDescription>
      </CardHeader>
      <CardContent>
        {hasUsage ? (
          <UsageChart dates={dates} totals={totals} />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No usage history available yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
